"use client";
import * as React from "react";

import Link from "next/link";

import { Button as HeadlessButton } from "@headlessui/react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/shared/helpers/cn.helper";

import Typography from "../typography/typography";

const baseClasses = cn(
  "flex flex-row items-center justify-center gap-2",
  "px-6 text-center text-sm font-bold",
  "rounded-xl",
  "cursor-pointer",
  "selection:bg-transparent",
  "focus:outline-none",
  "disabled:pointer-events-none",
);

const buttonVariants = cva(baseClasses, {
  variants: {
    variant: {
      white: [
        "border border-gray-300 bg-white text-text-primary",
        "hover:text-main-700",
        "focus-visible:text-main-800",
        "disabled:text-gray-200",
      ],
      ghost: [
        "bg-transparent",
        "hover:bg-transparent",
        "disabled:bg-transparent",
      ],
      icon: ["p-2", "hover:bg-light-100"],

      primary: [
        "bg-btn-primary fill-white stroke-white text-white",
        "hover:bg-btn-primary-hover",
        "active:bg-btn-primary-pressed",
        "disabled:bg-btn-primary-inactive-bg",
      ],
      secondary: [
        "bg-btn-secondary fill-gray-900 stroke-gray-900 text-text-primary",
        "hover:bg-btn-secondary-hover",
        "active:bg-btn-secondary-pressed",
        "disabled:bg-btn-secondary-inactive-bg disabled:fill-gray-300 disabled:stroke-gray-300 disabled:text-gray-300",
      ],
      tertiary: [
        "border border-border-2 bg-white fill-gray-900 stroke-gray-900 text-text-primary",
        "hover:bg-btn-tertiary-hover",
        "active:bg-gray-100",
        "disabled:bg-btn-tertiary-inactive disabled:fill-gray-200 disabled:stroke-gray-200 disabled:text-gray-200",
      ],
      danger: [
        "border border-system-red-light bg-white fill-system-red stroke-system-red text-system-red",
        "hover:fill-system-red-dark hover:stroke-system-red-dark hover:text-system-red-dark",
        "active:fill-system-red-super-dark active:stroke-system-red-super-dark active:text-system-red-super-dark",
        "disabled:fill-system-red-light disabled:stroke-system-red-light disabled:text-system-red-light",
      ],
      link: [
        "m-0 rounded-none border-none bg-transparent p-0 text-text-primary",
        "hover:text-btn-primary-hover",
        "active:text-btn-primary-pressed",
        "disabled:text-btn-primary-inactive-text",
      ],
    },
    iconVariant: {
      special: [
        "bg-btn-primary text-white",
        "hover:bg-btn-primary-hover",
        "active:bg-btn-primary-pressed",
        "disabled:bg-btn-primary-inactive-bg",
      ],
    },
    size: {
      lg: "py-[12px]",
      icon: "h-10 w-10",

      big: "h-12 max-h-12 min-h-12",
      small: "h-10.5 max-h-10.5 min-h-10.5",
    },
    typography: {
      label: [
        Typography.classes.label.default,
        Typography.classes.label.classnames,
      ],
      bodyMMedium: [
        Typography.classes.Body.default,
        Typography.classes.Body.classnames.mMedium,
      ],
      bodyMRegular: [
        Typography.classes.Body.default,
        Typography.classes.Body.classnames.mRegular,
      ],
    },
  },
  compoundVariants: [
    {
      variant: "icon",
      iconVariant: "special",
      className: [],
    },
    {
      variant: "link",
      size: "big",
      className: [
        Typography.classes.Body.default,
        Typography.classes.Body.classnames.mMedium,
      ],
    },
    {
      variant: "link",
      size: "small",
      className: [
        "border-main-600 w-fit border-b border-dashed",
        Typography.classes.Body.default,
        Typography.classes.Body.classnames.mRegular,
      ],
    },
  ],
  defaultVariants: {
    variant: "primary",
    size: "big",
    typography: "label",
  },
});
export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement | HTMLAnchorElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  as?: "button" | "a";
  href?: string;
}
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "primary",
      iconVariant,
      size = "big",
      disabled,
      as = "button",
      type = "button",
      typography,
      href,
      ...props
    },
    ref,
  ) => {

    if (href) {
      return (
        <HeadlessButton
          ref={ref as React.ForwardedRef<HTMLAnchorElement>}
          as={Link}
          className={cn(buttonVariants({ variant, iconVariant, typography, size, className }))}
          href={href}
          {...props}
        />
      );
    }

    return (
      <HeadlessButton
        ref={ref}
        as={as}
        className={cn(buttonVariants({ variant, iconVariant, typography, size, className }))}
        disabled={disabled}
        type={type}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";
export default Button;
export { Button, buttonVariants };
